import React from 'react'
import { Link } from 'react-router-dom'
import { api } from '../lib/api'
import { resolveAsyncBlobResponse } from '../lib/asyncOperation'
import Loading from '../ui/Loading'
import { useAuth } from '../auth/AuthContext'

const CATEGORY_OPTIONS = ['교통사고', '화재보험', '건강/진료', '일반', '기타']

function fmt(dt) {
    if (!dt) return '-'
    try {
        const d = new Date(dt)
        if (Number.isNaN(d.getTime())) return String(dt)
        const yyyy = d.getFullYear()
        const mm = String(d.getMonth() + 1).padStart(2, '0')
        const dd = String(d.getDate()).padStart(2, '0')
        const hh = String(d.getHours()).padStart(2, '0')
        const mi = String(d.getMinutes()).padStart(2, '0')
        return `${yyyy}-${mm}-${dd} ${hh}:${mi}`
    } catch {
        return String(dt)
    }
}

function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename || 'download'
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
}

export default function SalesImagesOrgTemplatesPage() {
    const { user } = useAuth()
    const [loading, setLoading] = React.useState(false)
    const [error, setError] = React.useState(null)
    const [items, setItems] = React.useState([])

    const isOperator = Boolean((user?.roles || []).includes('OPERATOR'))
    const [operatorLoadMode, setOperatorLoadMode] = React.useState('BY_ORG') // BY_ORG | ALL
    const [operatorOrgId, setOperatorOrgId] = React.useState('')
    const [operatorOrgOptions, setOperatorOrgOptions] = React.useState([])
    const [operatorOrgLoading, setOperatorOrgLoading] = React.useState(false)

    const [category, setCategory] = React.useState('')
    const [author, setAuthor] = React.useState('')

    const [customerInputs, setCustomerInputs] = React.useState({}) // tplId -> string
    const [modalOpen, setModalOpen] = React.useState(false)
    const [selected, setSelected] = React.useState(null)
    const [templateImgLoading, setTemplateImgLoading] = React.useState(false)
    const [templateImgUrl, setTemplateImgUrl] = React.useState(null)
    const [previewOpen, setPreviewOpen] = React.useState(false)
    const [previewImgUrl, setPreviewImgUrl] = React.useState(null)

    const load = React.useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const params = {}
            if (category) params.category = category
            if (author) params.author = author
            if (isOperator) {
                if (operatorLoadMode === 'ALL') {
                    params.viewAll = true
                } else {
                    if (!operatorOrgId) {
                        setItems([])
                        setError('운영자는 먼저 조직을 선택한 뒤 조회해주세요.')
                        return
                    }
                    params.orgId = Number(operatorOrgId)
                }
            }
            const res = await api.get('/sales-images/templates', { params })
            const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
            setItems(Array.isArray(payload) ? payload : [])
        } catch (_) {
            setError('목록을 불러오지 못했습니다.')
        } finally {
            setLoading(false)
        }
    }, [category, author, isOperator, operatorLoadMode, operatorOrgId])

    React.useEffect(() => {
        if (isOperator) return
        void load()
    }, [isOperator, load])

    React.useEffect(() => {
        if (!isOperator) return
        let ignore = false
        async function loadOpOrgs() {
            setOperatorOrgLoading(true)
            try {
                const res = await api.get('/orgs/options', { params: { scope: 'all' } })
                const payload = Array.isArray(res.data?.data) ? res.data.data : (Array.isArray(res.data) ? res.data : [])
                if (!ignore) setOperatorOrgOptions(Array.isArray(payload) ? payload : [])
            } catch (_) {
                if (!ignore) setOperatorOrgOptions([])
            } finally {
                if (!ignore) setOperatorOrgLoading(false)
            }
        }
        loadOpOrgs()
        return () => { ignore = true }
    }, [isOperator])

    const isExpired = (t) => {
        if (!t) return false
        if (t.isExpired === true) return true
        if (t.expiredAt) return true
        if (t.expiresAt) {
            try { return new Date(t.expiresAt).getTime() <= Date.now() } catch { }
        }
        return false
    }

    const openModal = async (tpl) => {
        setSelected(tpl)
        setModalOpen(true)
        setError(null)
        setTemplateImgUrl(prev => {
            try { if (prev) URL.revokeObjectURL(prev) } catch { }
            return null
        })

        if (!tpl?.imageFileId) return
        if (isExpired(tpl)) return

        setTemplateImgLoading(true)
        try {
            const res = await api.get(`/files/${tpl.imageFileId}/preview`, { responseType: 'blob' })
            const blob = new Blob([res.data], { type: res.headers?.['content-type'] || 'image/png' })
            const url = URL.createObjectURL(blob)
            setTemplateImgUrl(prev => {
                try { if (prev) URL.revokeObjectURL(prev) } catch { }
                return url
            })
        } catch (_) {
            setTemplateImgUrl(null)
        } finally {
            setTemplateImgLoading(false)
        }
    }
    const closeModal = () => {
        setModalOpen(false)
        setSelected(null)
        setTemplateImgLoading(false)
        setTemplateImgUrl(prev => {
            try { if (prev) URL.revokeObjectURL(prev) } catch { }
            return null
        })
    }

    const closePreview = () => {
        setPreviewOpen(false)
        setPreviewImgUrl(prev => {
            try { if (prev) URL.revokeObjectURL(prev) } catch { }
            return null
        })
    }

    const openPreview = async (tpl) => {
        if (!tpl?.id) return
        if (isExpired(tpl)) return setError('기한 만료된 템플릿입니다.')
        setLoading(true)
        setError(null)
        try {
            const res = await api.get(`/sales-images/templates/${tpl.id}/preview`, { responseType: 'blob' })
            const blob = new Blob([res.data], { type: res.headers?.['content-type'] || 'image/png' })
            const url = URL.createObjectURL(blob)
            setPreviewImgUrl(prev => {
                try { if (prev) URL.revokeObjectURL(prev) } catch { }
                return url
            })
            setPreviewOpen(true)
        } catch (_) {
            setError('미리보기를 불러오지 못했습니다.')
        } finally {
            setLoading(false)
        }
    }

    const downloadMine = async (tplId) => {
        try {
            const res = await api.get(`/sales-images/templates/${tplId}/download`, { responseType: 'blob' })
            const blob = new Blob([res.data], { type: res.headers?.['content-type'] || 'application/octet-stream' })
            const cd = res.headers?.['content-disposition'] || ''
            const m = /filename="?([^";]+)"?/i.exec(cd)
            const filename = m ? m[1] : `sales_image_${tplId}.png`
            downloadBlob(blob, filename)
            void load()
        } catch (e) {
            const st = e?.response?.status
            if (st === 410) return setError('기한 만료된 템플릿입니다.')
            setError('다운로드에 실패했습니다.')
        }
    }

    const downloadCustomersZip = async (tplId) => {
        const raw = customerInputs[tplId] || ''
        const customers = raw.split(/\r?\n/).map(s => s.trim()).filter(Boolean)
        if (customers.length === 0) return setError('고객명을 한 줄에 하나씩 입력하세요.')
        if (customers.length > 200) return setError('고객명은 최대 200명까지 가능합니다.')
        try {
            let res = await api.post(`/sales-images/templates/${tplId}/customers/zip`, { customers }, { responseType: 'blob' })
            res = await resolveAsyncBlobResponse(res)
            const blob = new Blob([res.data], { type: 'application/zip' })
            downloadBlob(blob, `customers_${tplId}.zip`)
            void load()
        } catch (e) {
            const st = e?.response?.status
            if (st === 410) return setError('기한 만료된 템플릿입니다.')
            setError('ZIP 다운로드에 실패했습니다.')
        }
    }

    return (
        <div className="space-y-5">
            <div className="flex flex-col gap-2">
                <Link className="text-sm text-slate-600 hover:text-brand-navy w-fit" to="/sales-images">
                    ← 카테고리 이미지 자료실
                </Link>
                <div>
                    <div className="text-2xl font-bold">조직별 업로드 템플릿</div>
                    <div className="text-sm text-slate-600 mt-1">소속 조직에 업로드된 영업용 이미지 템플릿을 조회합니다. (카테고리 자료실과 별도)</div>
                </div>
            </div>

            {error && <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
            {loading && <Loading />}

            <div className="rounded border bg-white p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {isOperator && (
                        <>
                            <div>
                                <div className="text-sm text-slate-600 mb-1">조회</div>
                                <select
                                    className="w-full rounded border px-3 py-2 text-sm"
                                    value={operatorLoadMode}
                                    onChange={(e) => {
                                        const v = e.target.value || 'BY_ORG'
                                        if (v === 'ALL') {
                                            const ok = window.confirm('전체보기는 모든 조직의 템플릿을 불러오므로 로딩이 오래 걸릴 수 있습니다.\n계속 진행할까요?')
                                            if (!ok) return
                                            setOperatorOrgId('')
                                        }
                                        setOperatorLoadMode(v)
                                    }}
                                >
                                    <option value="BY_ORG">조직별</option>
                                    <option value="ALL">전체보기</option>
                                </select>
                            </div>
                            <div>
                                <div className="text-sm text-slate-600 mb-1">조직</div>
                                <select
                                    className="w-full rounded border px-3 py-2 text-sm"
                                    value={operatorOrgId}
                                    disabled={operatorLoadMode === 'ALL' || operatorOrgLoading}
                                    onChange={(e) => setOperatorOrgId(e.target.value)}
                                >
                                    <option value="">{operatorOrgLoading ? '조직 불러오는 중...' : '조직 선택(필수)'}</option>
                                    {operatorOrgOptions.map(o => (
                                        <option key={o.id} value={o.id}>{o.name}</option>
                                    ))}
                                </select>
                            </div>
                        </>
                    )}
                    <div>
                        <div className="text-sm text-slate-600 mb-1">카테고리</div>
                        <select className="w-full rounded border px-3 py-2 text-sm" value={category} onChange={(e) => setCategory(e.target.value)}>
                            <option value="">전체</option>
                            {CATEGORY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                    <div>
                        <div className="text-sm text-slate-600 mb-1">작성자</div>
                        <input className="w-full rounded border px-3 py-2 text-sm" value={author} onChange={(e) => setAuthor(e.target.value)} placeholder="이름 검색" />
                    </div>
                    <div className="flex items-end">
                        <button type="button" className="rounded border px-3 py-2 text-sm hover:bg-slate-50" onClick={() => load()}>
                            필터 적용
                        </button>
                    </div>
                </div>
            </div>

            <div className="rounded border bg-white overflow-hidden">
                <div className="overflow-auto">
                    <table className="min-w-[980px] w-full text-sm">
                        <thead className="bg-slate-50 border-b">
                            <tr>
                                <th className="text-left px-4 py-3 font-semibold text-slate-700">카테고리</th>
                                <th className="text-left px-4 py-3 font-semibold text-slate-700">용도</th>
                                <th className="text-left px-4 py-3 font-semibold text-slate-700">작성자</th>
                                <th className="text-left px-4 py-3 font-semibold text-slate-700">생성일</th>
                                <th className="text-right px-4 py-3 font-semibold text-slate-700">생성수</th>
                                <th className="text-left px-4 py-3 font-semibold text-slate-700"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.length === 0 && (
                                <tr><td className="px-4 py-6 text-slate-500" colSpan={6}>등록된 템플릿이 없습니다.</td></tr>
                            )}
                            {items.map((t) => {
                                const canEdit = user?.id && t?.createdById && Number(user.id) === Number(t.createdById)
                                const expired = isExpired(t)
                                return (
                                    <tr key={t.id} className="border-b last:border-b-0">
                                        <td className="px-4 py-3">{t.category || '-'}</td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <div className="font-medium text-slate-800">{t.purpose || '-'}</div>
                                                {expired && (
                                                    <span className="inline-flex items-center rounded border border-rose-200 bg-rose-50 px-2 py-0.5 text-xs text-rose-700">
                                                        기한만료
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">{t.createdByName || '-'}</td>
                                        <td className="px-4 py-3">{fmt(t.createdAt)}</td>
                                        <td className="px-4 py-3 text-right">{typeof t.generatedCount === 'number' ? t.generatedCount : (t.generatedCount || 0)}</td>
                                        <td className="px-4 py-3">
                                            <button
                                                type="button"
                                                className={`rounded bg-brand-navy text-white px-3 py-1.5 hover:opacity-90 ${expired ? 'opacity-40 cursor-not-allowed' : ''}`}
                                                onClick={() => openModal(t)}
                                                disabled={expired}
                                            >
                                                생성
                                            </button>
                                            <button
                                                type="button"
                                                className={`ml-2 rounded border px-3 py-1.5 text-sm hover:bg-slate-50 ${expired ? 'opacity-40 cursor-not-allowed' : ''}`}
                                                onClick={() => openPreview(t)}
                                                disabled={expired}
                                            >
                                                미리보기
                                            </button>
                                            {canEdit && (
                                                <Link
                                                    className="ml-2 rounded border px-3 py-1.5 text-sm hover:bg-slate-50"
                                                    to={`/sales-images/${t.id}/edit`}
                                                >
                                                    수정
                                                </Link>
                                            )}
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {modalOpen && selected && (
                <div className="fixed inset-0 z-[100]">
                    <div className="absolute inset-0 bg-black/40" onClick={closeModal} />
                    <div className="absolute inset-0 flex items-center justify-center p-4">
                        <div className="w-full max-w-4xl rounded-lg bg-white shadow-lg border">
                            <div className="flex items-center justify-between px-4 py-3 border-b">
                                <div>
                                    <div className="font-semibold">{selected.purpose || selected.title}</div>
                                    <div className="text-xs text-slate-500 mt-0.5">
                                        {selected.category || '-'} · {selected.createdByName || '-'} · {fmt(selected.createdAt)}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    {user?.id && selected?.createdById && Number(user.id) === Number(selected.createdById) && (
                                        <Link className="rounded border px-3 py-1.5 text-sm hover:bg-slate-50" to={`/sales-images/${selected.id}/edit`} onClick={closeModal}>
                                            수정
                                        </Link>
                                    )}
                                    <button type="button" className="rounded border px-3 py-1.5 text-sm hover:bg-slate-50" onClick={closeModal}>
                                        닫기
                                    </button>
                                </div>
                            </div>

                            <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <div className="text-sm font-medium mb-2">템플릿</div>
                                    {isExpired(selected) ? (
                                        <div className="rounded border bg-rose-50 border-rose-200 px-3 py-2 text-sm text-rose-800">
                                            이 템플릿은 기한이 만료되어 비활성화되었습니다. (업로드 후 1개월 보관)
                                        </div>
                                    ) : (
                                        <>
                                            {templateImgLoading ? (
                                                <div className="rounded border bg-white p-3 text-sm text-slate-600">
                                                    불러오는 중...
                                                </div>
                                            ) : templateImgUrl ? (
                                                <img
                                                    src={templateImgUrl}
                                                    alt={selected.title}
                                                    className="w-full max-h-[520px] object-contain rounded border bg-slate-50"
                                                />
                                            ) : (
                                                <div className="rounded border bg-white p-3 text-sm text-slate-700">
                                                    이미지 미리보기를 불러오지 못했습니다.
                                                    <div className="text-xs text-slate-500 mt-2">
                                                        원본 파일: {selected.imageOriginalName || selected.imageFileId || '-'}
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>

                                <div className="space-y-3">
                                    {isExpired(selected) && (
                                        <div className="text-sm text-slate-700">
                                            <div>원본 파일: {selected.imageOriginalName || selected.imageFileId || '-'}</div>
                                            <div className="text-xs text-slate-500 mt-1">만료일: {fmt(selected.expiresAt)}</div>
                                        </div>
                                    )}
                                    {selected.messageTemplate ? (
                                        <>
                                            <div>
                                                <div className="text-sm font-medium mb-2">고객명 입력(한 줄에 1명)</div>
                                                <textarea
                                                    className="w-full rounded border px-3 py-2 text-sm min-h-[220px]"
                                                    value={customerInputs[selected.id] || ''}
                                                    onChange={(e) => setCustomerInputs(prev => ({ ...prev, [selected.id]: e.target.value }))}
                                                    placeholder={'예)\n홍길동\n김길동'}
                                                />
                                                <div className="text-xs text-slate-500 mt-2">최대 200명</div>
                                            </div>
                                            <button
                                                type="button"
                                                className={`w-full rounded bg-brand-navy text-white px-3 py-2 text-sm hover:opacity-90 ${isExpired(selected) ? 'opacity-40 cursor-not-allowed' : ''}`}
                                                onClick={() => downloadCustomersZip(selected.id)}
                                                disabled={isExpired(selected)}
                                            >
                                                다운로드(ZIP)
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <div className="text-sm text-slate-600">
                                                이 템플릿은 “내 이름 자동 삽입” 타입입니다.
                                            </div>
                                            <button
                                                type="button"
                                                className={`w-full rounded bg-brand-navy text-white px-3 py-2 text-sm hover:opacity-90 ${isExpired(selected) ? 'opacity-40 cursor-not-allowed' : ''}`}
                                                onClick={() => downloadMine(selected.id)}
                                                disabled={isExpired(selected)}
                                            >
                                                다운로드
                                            </button>
                                        </>
                                    )}
                                    <div className="text-xs text-slate-500">
                                        생성 카운트: {typeof selected.generatedCount === 'number' ? selected.generatedCount : (selected.generatedCount || 0)}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {previewOpen && previewImgUrl && (
                <div className="fixed inset-0 z-[120]">
                    <div className="absolute inset-0 bg-black/40" onClick={closePreview} />
                    <div className="absolute inset-0 flex items-center justify-center p-4">
                        <div className="w-full max-w-4xl rounded-lg bg-white shadow-lg border">
                            <div className="flex items-center justify-between px-4 py-3 border-b">
                                <div className="font-semibold">미리보기 (고객명: 홍길동)</div>
                                <button type="button" className="rounded border px-3 py-1.5 text-sm hover:bg-slate-50" onClick={closePreview}>
                                    닫기
                                </button>
                            </div>
                            <div className="p-4">
                                <img src={previewImgUrl} alt="preview" className="w-full max-h-[70vh] object-contain rounded border bg-slate-50" />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
